import React, { useCallback } from 'react';
import ModalDialog from '../../../../components/organisms/ModalDialog/ModalDialog';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import { Form, useForm, Textarea, SubmitButton, TextAreaParam, Row, SonForm, HiddenParam } from '../../../Form2';
import { Hint } from '../../Hint';
import { HiddenInput } from '../../../Form2/elements/HiddenInput/HiddenInput';
import { AxiosResponse } from 'axios';
import { useRefreshBlock } from '../../../BlockFetcher';
import { PostTypes } from '../Post.types';
import { useDelayed } from '../../../../hooks/useDelayed';

export type PostFlagInappropriateForm = SonForm<TextAreaParam<'reason'> | HiddenParam<'postId'>>;

type PostFlagInappropriateModalProps = {
  id: string;
  type: PostTypes;
  closeModal: () => void;
  form: PostFlagInappropriateForm;
};

export function PostFlagInappropriateModal({ id, type, closeModal, form }: PostFlagInappropriateModalProps) {
  const { t } = useSonTranslation('Posts');
  const { refresh } = useRefreshBlock();
  const delayedRefreshAndClose = useDelayed((newPostId: string) => {
    closeModal();
    refresh(newPostId);
  });
  const onSuccess = useCallback(
    ({ data: newPostId }: AxiosResponse<string>) => {
      delayedRefreshAndClose(newPostId);
    },
    [delayedRefreshAndClose]
  );

  const { formProps, fetchState, params } = useForm({
    form,
    onSuccess,
  });

  const reason = params.getByName('reason');
  const postIdHiddenParam = params.getByName('postId');

  return (
    <ModalDialog
      key={`flag-inappropriate-modal-${id}`}
      initialFocusIndex={1}
      closeModal={closeModal}
      headline={t(`default.contextMenu.flagInappropriate.${type}.title`)}
    >
      <Form {...formProps}>
        <Row>
          <Hint message={t('default.contextMenu.flagInappropriate.anonymousNote')} />
        </Row>
        <HiddenInput {...postIdHiddenParam} />
        <Row>
          <Textarea label={t('default.contextMenu.flagInappropriate.reasonLabel')} param={reason} autoExpand />
        </Row>
        <SubmitButton
          buttonLabel={t(`default.contextMenu.flagInappropriate.${type}.title`)}
          messageSending={t(`default.contextMenu.flagInappropriate.${type}.status.sending`)}
          messageSuccess={t(`default.contextMenu.flagInappropriate.${type}.status.success`)}
          messageFail={t(`default.contextMenu.flagInappropriate.${type}.status.fail`)}
          customErrorTypeMessages={{
            noModeratorAvailable: t('default.contextMenu.flagInappropriate.noModeratorAvailable'),
          }}
          fetchState={fetchState}
          // disable button while request is running:
          disabled={fetchState.type !== 'initial'}
        />
      </Form>
    </ModalDialog>
  );
}
